// src/SupplierForm.js ver 00 Nov 23 10:57 pm - SB113
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from './App';
import * as supplierService from './api/supplierService';
import { ApiError } from './api/apiClient';

const initialSupplierState = {
    companyName: '',
    contact: '',
    phone: '',
    SupplierCode: '', // NEW
    SiteAddress: '', // NEW
    OfficialEmail: '', // NEW
    OfficialWebsite: '', // NEW
};

function SupplierForm() {
    // const { tenantId, id: supplierId } = useParams();
    const { id: supplierId } = useParams();
    const navigate = useNavigate();
    const isEditing = Boolean(supplierId);
    const auth = useAuth();

    const [formData, setFormData] = useState(initialSupplierState);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [companyNameError, setCompanyNameError] = useState(null); // For uniqueness
    const [supplierCodeError, setSupplierCodeError] = useState(null); // For uniqueness

    const tenantId = auth.userProfile?.SubsCoDocId; // re-added ver01. existing in ver 00a
    console.log(`tenantId = ${tenantId}; supplierId = ${supplierId}`)

    useEffect(() => {
        const fetchData = async () => {
            if (!tenantId) {
                setError("Tenant ID missing in URL.");
                setLoading(false);
                return;
            }

            if (isEditing) {
                setLoading(true);
                setError(null);
                try {
                    const token = await auth.getIdToken();
                    if (!token) {
                        setError("Authentication required. Please log in.");
                        setLoading(false);
                        return;
                    }
                    const data = await supplierService.getSupplierById(supplierId, token, tenantId);
                    if (data) {
                        setFormData(data);
                    } else {
                        setError("Supplier not found.");
                        navigate(`/suppliers/tenant/${tenantId}`);
                    }
                } catch (err) {
                    console.error("Error fetching supplier from Cloud Function: ", err);
                    setError(`Failed to load supplier details: ${err.message}`);
                } finally {
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        };
        fetchData();
    }, [tenantId, supplierId, isEditing, navigate, auth]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setCompanyNameError(null);
        setSupplierCodeError(null);

        if (!tenantId) {
            setError("Tenant ID missing. Cannot save supplier.");
            return;
        }
        // Basic frontend validation for non-blank fields
        if (!formData.companyName || formData.companyName.trim() === '') {
            setCompanyNameError("Company Name is required.");
            return;
        }
        if (!formData.contact || formData.contact.trim() === '') {
            setError("Contact Person is required.");
            return;
        }
        if (!formData.phone || formData.phone.trim() === '') {
            setError("Phone is required.");
            return;
        }
        if (!formData.SupplierCode || formData.SupplierCode.trim() === '') {
            setSupplierCodeError("Supplier Code is required.");
            return;
        }
        if (!formData.OfficialEmail || !formData.OfficialEmail.includes('@') || formData.OfficialEmail.trim() === '') {
            setError("Official Email must be a valid email format.");
            return;
        }
        if (!formData.OfficialWebsite || (!formData.OfficialWebsite.startsWith('http://') && !formData.OfficialWebsite.startsWith('https://') && formData.OfficialWebsite.trim() !== '')) {
            setError("Official Website must be a valid URL format (e.g., https://example.com).");
            return;
        }
        
        try {
            const token = await auth.getIdToken();
            if (!token) {
                setError("Authentication required. Please log in.");
                return;
            }

            const payload = { ...formData };

            if (isEditing) {
                await supplierService.updateSupplier(supplierId, payload, token, tenantId);
                alert("Supplier updated successfully!");
            } else {
                await supplierService.addSupplier(payload, token, tenantId);
                alert("Supplier created successfully!");
            }
            navigate(`/suppliers`);
        } catch (err) {
            console.error("Error saving supplier via Cloud Function:", err);
            if (err instanceof ApiError && err.status === 409) {
                // Parse specific 409 messages to display correct error
                if (err.message.includes('Supplier Code')) {
                    setSupplierCodeError(err.message);
                } else if (err.message.includes('Company Name')) {
                    setCompanyNameError(err.message);
                } else {
                    setError(`Failed to save supplier: ${err.message}`);
                }
            } else {
                setError(`Failed to save supplier: ${err.message}`);
            }
        }
    };

    if (loading) return <p>Loading supplier details...</p>;
    if (error) return <p style={{ color: 'red' }}>Error: {error}</p>;
    if (!tenantId) return <p className="container" style={{ color: 'red' }}>Error: Tenant ID is required to add/edit supplier.</p>;


    return (
        <div className="container form-container">
            <h2>{isEditing ? `Edit Supplier` : `Add New Supplier`}</h2>
            <form onSubmit={handleSubmit}>
                {isEditing && formData.SeqId && (
                    <div className="form-group">
                        <label>Sequential ID</label>
                        <input type="text" name="SeqId" value={formData.SeqId || ''} disabled />
                    </div>
                )}
                <div className="form-group">
                    <label>Supplier Code</label>
                    <input
                        type="text"
                        name="SupplierCode"
                        value={formData.SupplierCode}
                        onChange={handleChange}
                        required
                        disabled={isEditing} // SupplierCode is not editable after creation
                    />
                    {supplierCodeError && <p style={{ color: 'red', fontSize: '0.85em' }}>{supplierCodeError}</p>}
                </div>
                <div className="form-group">
                    <label>Company Name</label>
                    <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        required
                    />
                    {companyNameError && <p style={{ color: 'red', fontSize: '0.85em' }}>{companyNameError}</p>}
                </div>
                <div className="form-group">
                    <label>Contact Person</label>
                    <input type="text" name="contact" value={formData.contact} onChange={handleChange} required />
                </div>
                <div className="form-group">
                    <label>Phone</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required />
                </div>
                <div className="form-group">
                    <label>Site Address</label>
                    <input type="text" name="SiteAddress" value={formData.SiteAddress} onChange={handleChange} />
                </div>
                <div className="form-group">
                    <label>Official Email</label>
                    <input type="email" name="OfficialEmail" value={formData.OfficialEmail} onChange={handleChange} required />
                </div>
                <div className="form-group">
                    <label>Official Website</label>
                    <input type="url" name="OfficialWebsite" value={formData.OfficialWebsite} onChange={handleChange} />
                </div>
                {isEditing && formData.DateAdded && (
                    <div className="form-group">
                        <label>Date Added</label>
                        <input type="text" value={new Date(formData.DateAdded._seconds * 1000).toLocaleString()} disabled />
                    </div>
                )}
                {isEditing && formData.DateLastModified && (
                    <div className="form-group">
                        <label>Date Last Modified</label>
                        <input type="text" value={new Date(formData.DateLastModified._seconds * 1000).toLocaleString()} disabled />
                    </div>
                )}

                <button type="submit">Save Supplier</button>
            </form>
        </div>
    );
}

export default SupplierForm;